import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ROUTES } from '@/router';

import { useFeature } from '../api';
import type { FeatureKey } from '../types';

/**
 * docs/BILLING.md §5 — a page or panel that needs a package feature. Without it the user reads
 * what the feature is and which package they are on, with a way to the packages. The API
 * refuses the feature's writes on its own; this only spares the user a page of errors.
 */
export function PlanGate({
  feature,
  children,
}: {
  feature: FeatureKey;
  children: ReactNode;
}): JSX.Element {
  const { allowed } = useFeature(feature);
  return allowed ? <>{children}</> : <LockedFeature feature={feature} />;
}

export function LockedFeature({ feature }: { feature: FeatureKey }): JSX.Element {
  const { t } = useTranslation();
  const { me } = useFeature(feature);
  const name = t(`billing.features.${feature}`);
  return (
    <Card padding="lg" className="plan-locked mx-auto mt-6 max-w-xl text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-brand-tint text-brand">
        <Lock aria-hidden="true" size={18} strokeWidth={1.6} />
      </div>
      <h2 className="text-title font-semibold text-ink">{t('billing.gate.title', { feature: name })}</h2>
      <p className="mt-2 text-body text-ink-secondary">{t(`billing.gate.why.${feature}`)}</p>
      {me?.plan && (
        <p className="mt-2 text-caption text-ink-tertiary">
          {t('billing.gate.current', { plan: me.plan.name })}
        </p>
      )}
      <div className="mt-4 flex justify-center">
        <Button asChild>
          <Link to={ROUTES.billing}>{t('billing.gate.cta')}</Link>
        </Button>
      </div>
    </Card>
  );
}
